import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard,
  FileText,
  Search,
  Target,
  Bookmark,
  Globe,
  Briefcase,
  Settings,
  LogOut,
  Languages,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sliders,
  Filter,
  MessageSquare,
  Mic,
  PenTool,
  BrainCircuit,
  Linkedin,
  LineChart,
  Banknote,
  Lightbulb,
  Building,
  Sparkles,
  Check,
  Rocket
} from 'lucide-react';
import toastUtil from '../../utils/toast';

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
];

export default function Sidebar({ isCollapsed, setIsCollapsed }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, isRTL } = useTheme();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [jobSearchOpen, setJobSearchOpen] = useState(false);
  const [aiDocsOpen, setAiDocsOpen] = useState(true);
  const [aiCareerOpen, setAiCareerOpen] = useState(true);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sidebarTranslations = {
    en: {
      overview: 'Overview',
      cvBuilder: 'CV Builder',
      jobSearch: 'Job Search',
      freeJobSearch: 'Free Job Search',
      savedJobs: 'Job Library',
      remoteJobs: 'Remote Jobs',
      advancedSearch: 'Advanced Search',
      coverLetters: 'Cover Letters',
      chatbot: 'AI Agent',
      interviewPrep: 'Interview Simulator',
      coverLetterGen: 'Cover Letter Generator',
      atsMatcher: 'ATS Keyword Matcher',
      linkedinOpt: 'LinkedIn Optimizer',
      settings: 'Settings',
      logout: 'Logout',
      language: 'Language',
      portfolioBuilder: 'Portfolio Builder',
      upcomingFeatures: 'Upcoming Features'
    },
    ar: {
      overview: 'نظرة عامة',
      cvBuilder: 'منشئ السيرة الذاتية',
      jobSearch: 'البحث عن وظائف',
      freeJobSearch: 'بحث مجاني عن وظائف',
      savedJobs: 'مكتبة الوظائف',
      remoteJobs: 'وظائف عن بعد',
      advancedSearch: 'بحث متقدم',
      coverLetters: 'رسائل التغطية',
      chatbot: 'وكيل الذكاء الاصطناعي',
      interviewPrep: 'محاكي المقابلات',
      coverLetterGen: 'صانع رسائل التغطية',
      atsMatcher: 'مطابق الـ ATS',
      linkedinOpt: 'مُحسن لينكد إن',
      settings: 'الإعدادات',
      logout: 'تسجيل الخروج',
      language: 'اللغة',
      portfolioBuilder: 'منشئ معرض الأعمال',
      upcomingFeatures: 'المميزات القادمة'
    },
    tr: {
      overview: 'Genel Bakış',
      cvBuilder: 'CV Oluşturucu',
      jobSearch: 'İş Arama',
      freeJobSearch: 'Ücretsiz İş Arama',
      savedJobs: 'İş Kütüphanesi',
      remoteJobs: 'Uzaktan İşler',
      advancedSearch: 'Gelişmiş Arama',
      coverLetters: 'Ön Yazılar',
      chatbot: 'Yapay Zeka Ajanı',
      interviewPrep: 'Mülakat Simülatörü',
      coverLetterGen: 'Ön Yazı Oluşturucu',
      atsMatcher: 'ATS Anahtar Kelime Eşleştirici',
      linkedinOpt: 'LinkedIn Optimize Edici',
      settings: 'Ayarlar',
      logout: 'Çıkış Yap',
      language: 'Dil',
      portfolioBuilder: 'Portföy Oluşturucu',
      upcomingFeatures: 'Yakında Gelecekler'
    }
  };

  const tr = sidebarTranslations[language] || sidebarTranslations['en'];

  const jobSearchSubItems = [
    { id: 'free-job-search', icon: Search, label: tr.freeJobSearch, path: '/job-search' },
    { id: 'remote-jobs-sub', icon: Globe, label: tr.remoteJobs, path: '/remote-jobs' },
    { id: 'advanced-search-sub', icon: Filter, label: tr.advancedSearch, path: '/advanced-search' },
  ];

  const coreItems = [
    { id: 'overview', icon: LayoutDashboard, label: tr.overview, path: '/dashboard' },
    { id: 'cv-builder', icon: FileText, label: tr.cvBuilder, path: '/cv-builder' },
    { id: 'job-search', icon: Search, label: tr.jobSearch, hasSubmenu: true },
    { id: 'saved-jobs', icon: Bookmark, label: tr.savedJobs, path: '/job-library' },
    { id: 'cover-letters', icon: FileText, label: tr.coverLetters, path: '/cover-letters' },
  ];

  const aiDocumentItems = [
    { id: 'cover-letter-gen', icon: PenTool, label: tr.coverLetterGen, path: '/cover-letter-generator' },
    { id: 'portfolio-builder', icon: Globe, label: tr.portfolioBuilder, path: '/portfolio-builder' },
    { id: 'ats-matcher', icon: Search, label: tr.atsMatcher, path: '/ats-keyword-matcher' },
    { id: 'linkedin-opt', icon: Linkedin, label: tr.linkedinOpt, path: '/linkedin-optimizer' },
  ];

  const aiCareerItems = [
    { id: 'chatbot', icon: MessageSquare, label: tr.chatbot, path: '/chatbot' },
    { id: 'interview-prep', icon: Mic, label: tr.interviewPrep, path: '/interview-prep' },
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const isJobSearchActive = () => {
    return location.pathname.startsWith('/job-search') ||
           location.pathname.startsWith('/remote-jobs') ||
           location.pathname.startsWith('/advanced-search');
  };

  async function handleLogout() {
    try {
      await logout();
      toastUtil.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toastUtil.error('Failed to logout');
    }
  }

  function handleSelectLanguage(code) {
    setLanguage(code);
    setLangDropdownOpen(false);
    const labels = { en: 'Switched to English', ar: 'تم التبديل إلى العربية', tr: 'Türkçeye geçildi' };
    toastUtil.success(labels[code] || 'Language changed');
  }

  const currentLangOption = LANGUAGE_OPTIONS.find(l => l.code === language) || LANGUAGE_OPTIONS[0];

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = item.path && isActivePath(item.path);
    const isDanger = item.danger;
    const hasSubmenu = item.hasSubmenu;
    const isSubmenuActive = item.id === 'job-search' && isJobSearchActive();

    const handleMouseEnter = (e) => {
      setHoveredItem(item.id);
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: isRTL ? rect.left - 4 : rect.right + 4
      });
    };

    const handleClick = () => {
      if (hasSubmenu) {
        setJobSearchOpen(!jobSearchOpen);
      } else if (item.action) {
        item.action();
      }
    };

    const content = (
      <motion.div
        className={`
          relative flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer group transition-all duration-200
          ${isActive || isSubmenuActive
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20'
            : isDanger
            ? 'text-rose-500 hover:bg-rose-50/50'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          }
          ${isCollapsed ? 'justify-center px-0' : ''}
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHoveredItem(null)}
        whileHover={{ scale: (isActive || isSubmenuActive) ? 1 : 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {(isActive || isSubmenuActive) && (
          <motion.div
            layoutId="activeIndicator"
            className={`absolute top-2 bottom-2 w-1 bg-white ${isRTL ? 'right-0 rounded-l-full' : 'left-0 rounded-r-full'}`}
            initial={false}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        )}

        <Icon
          className={`
            w-4 h-4 flex-shrink-0 transition-colors duration-200
            ${(isActive || isSubmenuActive)
              ? 'text-white'
              : isDanger
              ? 'text-rose-500'
              : 'text-slate-400 group-hover:text-slate-600'
            }
          `}
        />

        <AnimatePresence mode="wait">
          {!isCollapsed && (
            <span
              className={`text-[13px] font-semibold whitespace-nowrap overflow-hidden flex-1 ${
                (isActive || isSubmenuActive) ? 'text-white' : isDanger ? 'text-rose-600' : 'text-slate-700'
              }`}
            >
              {item.label}
            </span>
          )}
        </AnimatePresence>

        {hasSubmenu && !isCollapsed && (
          <motion.div
            animate={{ rotate: jobSearchOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className={`w-3.5 h-3.5 ${(isActive || isSubmenuActive) ? 'text-white' : 'text-slate-400'}`} />
          </motion.div>
        )}
      </motion.div>
    );

    if (item.path) {
      return (
        <Link key={item.id} to={item.path} className="block no-underline">
          {content}
        </Link>
      );
    }

    return (
      <div key={item.id} onClick={handleClick}>
        {content}
      </div>
    );
  };

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? '78px' : '270px' }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className={`relative bg-white/80 backdrop-blur-md ${isRTL ? 'border-l' : 'border-r'} border-[#EBF0FA] flex flex-col h-screen sticky top-0 z-30 shadow-[4px_0_24px_rgba(0,0,0,0.02)] overflow-visible`}
    >
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`absolute top-6 ${isRTL ? '-left-3.5' : '-right-3.5'} w-7 h-7 rounded-full bg-white border border-[#EBF0FA] flex items-center justify-center cursor-pointer shadow-md z-50 hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors`}
        title={isCollapsed ? (isRTL ? 'توسيع القائمة' : 'Expand Sidebar') : (isRTL ? 'طي القائمة' : 'Collapse Sidebar')}
      >
        {isCollapsed ? (
          isRTL ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />
        ) : (
          isRTL ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />
        )}
      </button>

      <div className="py-6 border-b border-[#EBF0FA] flex items-center justify-center">
        <Link to="/dashboard" className="flex items-center gap-2.5 no-underline">
          <img
            src="/cvlogs-logo.png"
            alt="CVLogs"
            className="w-9 h-9 object-contain flex-shrink-0"
            style={{ imageRendering: 'crisp-edges' }}
          />
          <AnimatePresence mode="wait">
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="text-lg font-black text-slate-800 tracking-tight whitespace-nowrap overflow-hidden"
              >
                CV<span className="text-blue-600 font-extrabold">Logs</span>
              </motion.span>
            )}
          </AnimatePresence>
        </Link>
      </div>

      <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto overflow-x-visible professional-scrollbar">
        <div className="mb-5">
          {!isCollapsed && (
            <div className="px-3 py-1.5 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
              {language === 'ar' ? 'الرئيسية' : language === 'tr' ? 'Ana Sayfa' : 'Main Menu'}
            </div>
          )}
          {coreItems.map(item => (
            <div key={item.id}>
              {renderNavItem(item)}

              <AnimatePresence>
                {item.hasSubmenu && jobSearchOpen && !isCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mt-1 ms-2"
                  >
                    <div className="bg-slate-50/50 rounded-2xl p-1.5 space-y-1 border border-slate-100 shadow-inner">
                      {jobSearchSubItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = isActivePath(subItem.path);

                        return (
                          <Link key={subItem.id} to={subItem.path} className="block no-underline">
                            <motion.div
                              className={`
                                relative flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer transition-all duration-200
                                ${isSubActive
                                  ? 'bg-white text-blue-600 shadow-sm border border-slate-100 font-bold'
                                  : 'text-slate-500 hover:bg-white/80 hover:text-slate-800'
                                }
                              `}
                              whileHover={{ x: isSubActive ? 0 : 3 }}
                            >
                              <SubIcon className={`w-3.5 h-3.5 ${isSubActive ? 'text-blue-600' : 'text-slate-400'}`} />
                              <span className="text-[12px] font-semibold">{subItem.label}</span>
                            </motion.div>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <div className="mb-5">
          {!isCollapsed && (
            <div
              onClick={() => setAiDocsOpen(!aiDocsOpen)}
              className="px-3 py-1.5 text-[10px] font-extrabold flex items-center justify-between text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-50 rounded-xl transition-colors"
            >
              <span>{language === 'ar' ? 'أدوات المستندات' : language === 'tr' ? 'Belge Araçları' : 'Document AI'}</span>
              <motion.div animate={{ rotate: aiDocsOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </motion.div>
            </div>
          )}
          <AnimatePresence>
            {(aiDocsOpen || isCollapsed) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden space-y-0.5"
              >
                {aiDocumentItems.map(item => renderNavItem(item))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mb-5">
          {!isCollapsed && (
            <div
              onClick={() => setAiCareerOpen(!aiCareerOpen)}
              className="px-3 py-1.5 text-[10px] font-extrabold flex items-center justify-between text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-50 rounded-xl transition-colors"
            >
              <span>{language === 'ar' ? 'مساعد المهنة' : language === 'tr' ? 'Kariyer Asistanı' : 'Career AI'}</span>
              <motion.div animate={{ rotate: aiCareerOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </motion.div>
            </div>
          )}
          <AnimatePresence>
            {(aiCareerOpen || isCollapsed) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden space-y-0.5"
              >
                {aiCareerItems.map(item => renderNavItem(item))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="pt-2 border-t border-[#EBF0FA]">
          <Link to="/upcoming-features" className="block no-underline">
            <motion.div
              className={`
                relative flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-all duration-200
                ${isActivePath('/upcoming-features')
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-blue-50/50 hover:bg-blue-50 text-blue-600 border border-blue-100/50'
                }
                ${isCollapsed ? 'justify-center px-0' : ''}
              `}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onMouseEnter={(e) => {
                setHoveredItem('upcoming');
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltipPosition({
                  top: rect.top + rect.height / 2,
                  left: isRTL ? rect.left - 4 : rect.right + 4
                });
              }}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <Rocket className={`w-4 h-4 flex-shrink-0 ${isActivePath('/upcoming-features') ? 'text-white' : 'text-blue-500 animate-pulse'}`} />
              {!isCollapsed && (
                <span className={`text-[13px] font-bold flex-1 ${isActivePath('/upcoming-features') ? 'text-white' : 'text-blue-700'}`}>
                  {tr.upcomingFeatures}
                </span>
              )}
              {!isCollapsed && (
                <span className="text-[9px] font-extrabold uppercase tracking-wide bg-blue-600 text-white rounded-full px-1.5 py-0.5 leading-none scale-90">
                  New
                </span>
              )}
            </motion.div>
          </Link>
        </div>
      </nav>

      <div className="p-4 border-t border-[#EBF0FA] space-y-1">
        <div ref={langDropdownRef} className="relative">
          <motion.button
            onClick={() => setLangDropdownOpen(!langDropdownOpen)}
            className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-all
              text-slate-600 hover:bg-slate-50 hover:text-slate-900
              ${isCollapsed ? 'justify-center px-0' : ''}
            `}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            title={isCollapsed ? (language === 'ar' ? 'اللغة' : 'Language') : undefined}
          >
            <Languages className="w-4 h-4 flex-shrink-0 text-slate-400" />
            <AnimatePresence mode="wait">
              {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 text-[13px] font-semibold text-slate-700 text-left flex items-center gap-2"
                >
                  <span className="text-base leading-none">{currentLangOption.flag}</span>
                  <span>{currentLangOption.label}</span>
                </motion.span>
              )}
            </AnimatePresence>
            {!isCollapsed && (
              <motion.div animate={{ rotate: langDropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </motion.div>
            )}
          </motion.button>

          <AnimatePresence>
            {langDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className={`
                  absolute bottom-full mb-2 bg-white border border-slate-100 rounded-2xl shadow-xl overflow-hidden z-50
                  ${isCollapsed ? (isRTL ? 'right-0' : 'left-full ml-2 bottom-0 mb-0') : 'left-0 right-0'}
                `}
                style={{ minWidth: isCollapsed ? '160px' : undefined }}
              >
                <div className="p-1.5">
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => handleSelectLanguage(opt.code)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                        ${language === opt.code
                          ? 'bg-blue-50 text-blue-700 font-bold'
                          : 'text-slate-700 hover:bg-slate-50'
                        }
                      `}
                    >
                      <span className="text-lg leading-none">{opt.flag}</span>
                      <span className="flex-1 text-left">{opt.label}</span>
                      {language === opt.code && (
                        <Check className="w-3.5 h-3.5 text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <Link to="/settings" className="block no-underline">
          <motion.div
            className={`
              flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-all duration-200
              ${isActivePath('/settings') ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
              ${isCollapsed ? 'justify-center px-0' : ''}
            `}
            whileHover={{ scale: isActivePath('/settings') ? 1 : 1.01 }}
            whileTap={{ scale: 0.98 }}
          >
            <Settings className={`w-4 h-4 flex-shrink-0 ${isActivePath('/settings') ? 'text-white' : 'text-slate-400'}`} />
            {!isCollapsed && (
              <span className={`text-[13px] font-semibold whitespace-nowrap overflow-hidden flex-1 ${isActivePath('/settings') ? 'text-white' : 'text-slate-700'}`}>
                {tr.settings}
              </span>
            )}
          </motion.div>
        </Link>

        <motion.div
          onClick={handleLogout}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-all
            text-rose-600 hover:bg-rose-50/50
            ${isCollapsed ? 'justify-center px-0' : ''}
          `}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
        >
          <LogOut className="w-4 h-4 flex-shrink-0 text-rose-500" />
          {!isCollapsed && (
            <span className="text-[13px] font-semibold whitespace-nowrap overflow-hidden flex-1 text-rose-600">
              {tr.logout}
            </span>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {isCollapsed && hoveredItem && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: -10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.95, x: -10 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: `translate(${isRTL ? '-100%' : '0'}, -50%)`,
              zIndex: 9999
            }}
            className="bg-slate-900 text-white px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap shadow-xl pointer-events-none border border-slate-800 text-left"
          >
            {hoveredItem === 'upcoming' 
              ? tr.upcomingFeatures 
              : [...coreItems, ...aiDocumentItems, ...aiCareerItems].find(item => item.id === hoveredItem)?.label
                || (hoveredItem === 'settings' ? tr.settings : tr.logout)}
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-0 h-0 border-[5px] border-transparent ${
                isRTL
                  ? 'right-full border-r-slate-900'
                  : 'left-full border-l-slate-900'
              }`}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.aside>
  );
}